import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { getServiceRequest } from "../../commons/ServiceRequest";
import { initializeSetOption } from "../../utils/InitializeSetDefault";
import {
  concatenatedLabels,
  filteredItemBySearch,
  hasValueObjects
} from "../../utils/ManageArray";
import { formatDate } from "../../commons/FormatValue";
import TableForm from "../TableForm";
import SearchFilter from "../SearchFilter";
import ConfirmModal from "../ConfirmModal";
import CardDisplayComponent from "../CardDisplayComponent";
import SeqRepairJobsService from "../../services/seqrepairjob.service";
import SequenceStatusService from "../../services/sequencestatus.service";

const EditWorkOrderStatusForm = ({ onSubmitCallback }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const defaultData = {
    com_code: user?.Com_Code,
    inform_repair_id: "",
    seq_repair_no: "",
    job_type_id: "",
    seq_status_id: "",
    seq_repair_date: "",
    job_start_date: "",
    job_end_date: ""
  };
  const [data, setData] = useState([defaultData]);
  const [Repair, setRepair] = useState({ Job: [], Status: [] });
  const searchInput = [
    {
      name: "seq_repair_date",
      label: "วันที่ออกใบสั่งงาน",
      type: "date",
      dateOperator: ">="
    },
    {
      name: "seq_repair_no",
      label: "เลขที่ใบสั่งแจ้งซ่อม",
      filterBy: ["seq_repair_no"]
    }
  ];
  const columns = [
    {
      name: "seq_repair_date",
      label: "วันที่ออกใบสั่งงาน",
      readOnly: true,
      format: (row) => formatDate(row?.seq_repair_date)
    },
    { name: "seq_repair_no", label: "เลขที่ใบสั่งงาน", readOnly: true },
    {
      name: "job_type_items.Job_Type_Name",
      label: "ประเภทงาน",
      readOnly: true
    },
    {
      name: "inform_repair_items.concern_customer_items.Cus_Code",
      label: "รหัสลูกค้า/สาขา",
      readOnly: true
    },
    {
      name: "inform_repair_items.concern_customer_items.Cus_Name",
      label: "ชื่อลูกค้า/สาขา",
      readOnly: true
    },
    {
      name: "seq_status_id",
      label: "สถานะใบสั่งงาน",
      type: "select",
      noneAsync: true,
      filterBy: ["Seq_St_Name"],
      options: initializeSetOption({
        key: "Id",
        label: "Seq_St_Name",
        dataset: Repair.Status
      })
    }
  ];

  const checkValidity = () => {
    const checkKey = ["id", "seq_status_id"];
    const { hasValue, emptyKey } = hasValueObjects(data, checkKey);
    if (hasValue?.length > 0) {
      const sumEmptyKey = concatenatedLabels(emptyKey, columns);
      showAlert({
        error: "ข้อผิดพลาด",
        message: `กรุณากรอกข้อมูลให้ครบถ้วน (${sumEmptyKey})`,
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else setShowModal(true);
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      const modifiedData = dataCallback?.data.map(({ id, seq_status_id }) => ({
        id,
        seq_status_id: Number(seq_status_id)
      }));
      onSubmitCallback(modifiedData);
    }
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: SeqRepairJobsService.getAllSeqRepairJob(),
      callback: (res) => setRepair((prev) => ({ ...prev, Job: res }))
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: SequenceStatusService.getAllSequenceStatuses(),
      callback: (res) => setRepair((prev) => ({ ...prev, Status: res }))
    });
  }, []);

  return (
    <>
      <CardDisplayComponent
        header={{
          title: { text: "แก้ไขสถานะใบสั่งงาน", className: "text-center" }
        }}
        body={{
          element: (
            <SearchFilter
              data={Repair.Job}
              inputField={searchInput}
              fieldClassName={{
                inputRow: "justify-content-center align-items-center",
                actionButton: "w-auto"
              }}
              searchCallback={(search) => {
                setData(
                  search.some(({ value }) => value)
                    ? filteredItemBySearch(Repair.Job, search)
                    : [defaultData]
                );
              }}
            />
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดอุปกรณ์ที่แจ้งซ่อม" } }}
          body={{
            element: (
              <TableForm
                data={data}
                columns={columns}
                pending={loading}
                onChangeCallback={(newData) => setData(newData)}
              />
            )
          }}
          footer={{
            className: "text-center",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  บันทึกการแก้ไข
                </Button>

                <ConfirmModal
                  show={showModal}
                  data={{ data }}
                  content={"save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  type="button"
                  variant="outline-secondary"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                  onClick={() => navigate(-1)}
                >
                  ย้อนกลับ
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};
EditWorkOrderStatusForm.propTypes = {
  onSubmitCallback: PropTypes.func
};

export default EditWorkOrderStatusForm;
